import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { contentText, isVerdictValue, ROLE_TEXT } from 'features/datasets/utils/units';

/**
 * How a row's Input and Output read in the drawer.
 *
 *   Transcript   one block per turn: the role, the text, images, tool calls.
 *                System turns are muted; tool turns are monospace.
 *   OutputBlock  the assistant message: text, tool calls, structured JSON.
 *   VerdictBlock an eval verdict (Output or Expected of a verdict row):
 *                Pass/Fail, reason, the step answers.
 *
 * Each block folds, and offers Raw for the exact JSON.
 */

function Fold({ title, count, children, raw, defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className="ds-accordion" data-testid={testId}>
      <div className="ds-accordion-header">
        <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1" aria-expanded={open}>
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <span>{title}</span>
          {count != null ? <span className="text-[color:var(--text-muted)]">{count}</span> : null}
        </button>
        {open && raw !== undefined ? (
          <button type="button" onClick={() => setShowRaw((v) => !v)} aria-pressed={showRaw}
            className="ml-auto text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">{showRaw ? 'Formatted' : 'Raw'}</button>
        ) : null}
      </div>
      {open ? (showRaw ? <pre className="ds-code-pre ds-code-wrap px-3 pb-3 text-[11px]">{JSON.stringify(raw, null, 2)}</pre> : children) : null}
    </div>
  );
}

function Parts({ content }) {
  if (content == null) return null;
  if (typeof content === 'string') return <div className="ds-turn-body">{content}</div>;
  if (!Array.isArray(content)) return <div className="ds-turn-body">{JSON.stringify(content, null, 2)}</div>;
  return (
    <div className="ds-turn-body">
      {content.map((p, i) => {
        if (!p) return null;
        if (typeof p === 'string') return <div key={i}>{p}</div>;
        if (p.type === 'text') return <div key={i}>{p.text}</div>;
        if (p.type === 'image') {
          const url = p.url || p.image_url?.url || p.source?.url || null;
          return url && /^https?:/.test(url) ? <img key={i} src={url} alt="" className="ds-turn-image" /> : <div key={i} className="text-[color:var(--text-muted)]">[image]</div>;
        }
        if (p.type === 'json') return <pre key={i} className="ds-tool-call">{JSON.stringify(p.value, null, 2)}</pre>;
        return <div key={i}>{p.text || JSON.stringify(p)}</div>;
      })}
    </div>
  );
}

function ToolCalls({ calls }) {
  if (!Array.isArray(calls) || !calls.length) return null;
  return calls.map((t, i) => {
    const name = t.function?.name || t.name || 'tool';
    const args = t.function?.arguments ?? t.arguments;
    const text = typeof args === 'string' ? args : JSON.stringify(args ?? {}, null, 2);
    return <pre key={t.id || i} className="ds-tool-call">{name}({text})</pre>;
  });
}

function Turn({ m }) {
  const role = ROLE_TEXT[m.role] || m.role || 'message';
  return (
    <div className={`ds-turn ds-turn-${role}`}>
      <div className="ds-turn-head">
        <span className={`ds-role ds-role-${role}`}>{role}</span>
        {m.name ? <span className="text-[11px] text-[color:var(--text-muted)]">{m.name}</span> : null}
      </div>
      <Parts content={m.content} />
      <ToolCalls calls={m.tool_calls} />
    </div>
  );
}

/** The input transcript. */
export function Transcript({ messages, title = 'Input' }) {
  const list = Array.isArray(messages) ? messages.filter(Boolean) : [];
  if (!list.length) return null;
  return (
    <Fold title={title} count={`${list.length} turn${list.length === 1 ? '' : 's'}`} raw={list} testId="transcript">
      <div>{list.map((m, i) => <Turn key={i} m={m} />)}</div>
    </Fold>
  );
}

/** The verdict an eval gave, or the correction of one. */
export function VerdictBlock({ title, value }) {
  if (!isVerdictValue(value)) return null;
  const steps = Object.entries(value.steps || {});
  return (
    <Fold title={title} raw={value} testId="verdict-block">
      <dl className="ds-verdict-grid">
        <dt>Verdict</dt>
        <dd className={value.passed ? 'text-success' : 'text-danger'}>{value.passed === true ? 'Pass' : value.passed === false ? 'Fail' : '—'}</dd>
        {value.reason_code ? <><dt>Reason</dt><dd>{value.reason_code}</dd></> : null}
        {typeof value.score === 'number' ? <><dt>Score</dt><dd>{value.score.toFixed(2)}</dd></> : null}
        {Array.isArray(value.path) && value.path.length ? <><dt>Path</dt><dd className="!font-sans">{value.path.join(' › ')}</dd></> : null}
        {value.fix_kind === 'eval' ? <><dt>Fix</dt><dd className="!font-sans">Every step was right: a check is missing.</dd></> : null}
        {steps.map(([node, v]) => (
          <React.Fragment key={node}>
            <dt>{node}</dt>
            <dd>{v && typeof v === 'object' ? Object.entries(v).map(([k, x]) => `${k} = ${String(x)}`).join(', ') : String(v)}</dd>
          </React.Fragment>
        ))}
      </dl>
    </Fold>
  );
}

/** What the unit produced: the assistant message. */
export function OutputBlock({ output, title = 'Output' }) {
  if (output == null) return null;
  if (isVerdictValue(output)) return <VerdictBlock title={title} value={output} />;
  const msg = typeof output === 'string' ? { role: 'assistant', content: output } : output;
  const isMessage = msg && typeof msg === 'object' && !Array.isArray(msg) && ('content' in msg || 'tool_calls' in msg);
  return (
    <Fold title={title} raw={output} testId="output-block">
      {isMessage ? (
        <div className="ds-turn ds-turn-assistant">
          <Parts content={msg.content} />
          <ToolCalls calls={msg.tool_calls} />
          {!contentText(msg.content) && !(msg.tool_calls && msg.tool_calls.length) ? <div className="ds-turn-body text-[color:var(--text-muted)]">Empty</div> : null}
        </div>
      ) : <pre className="ds-code-pre ds-code-wrap px-3 pb-3 text-[11px]">{JSON.stringify(output, null, 2)}</pre>}
    </Fold>
  );
}
